import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import Info from "../Components/Info";
import AskForm from "../Components/AskForm";
import HeightInterval from "../Components/HeightInterval.jsx";
import useStore from '../store.js';
import ChatContainer from './ChatContainer';
import ChatSearchHistory from './ChatSearchHistory.jsx';
import BannerMobile from './BannerMobile.jsx';
import {
  observeChatResize,
  scheduleChatScrollToBottom,
} from '../utils/chatScroll.js';
import { lockDocumentScroll } from '../utils/documentScrollLock.js';

const useScreenWidth = () => {
  const [width, setWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return width;
};

const getKeyboardViewport = () => {
  const layoutHeight = window.innerHeight || document.documentElement.clientHeight || 0;
  const visualViewport = window.visualViewport;

  if (!visualViewport) {
    return {
      keyboardInset: 0,
      viewportHeight: layoutHeight,
      viewportOffsetTop: 0,
    };
  }

  return {
    keyboardInset: Math.max(
      0,
      Math.round(layoutHeight - visualViewport.height - visualViewport.offsetTop),
    ),
    viewportHeight: Math.max(0, Math.round(visualViewport.height || layoutHeight)),
    viewportOffsetTop: Math.max(0, Math.round(visualViewport.offsetTop || 0)),
  };
};

const useKeyboardViewport = () => {
  const [keyboardViewport, setKeyboardViewport] = useState(getKeyboardViewport);

  useEffect(() => {
    let frameId = 0;

    const updateKeyboardViewport = () => {
      window.cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(() => {
        setKeyboardViewport(getKeyboardViewport());
      });
    };

    updateKeyboardViewport();
    window.visualViewport?.addEventListener("resize", updateKeyboardViewport);
    window.visualViewport?.addEventListener("scroll", updateKeyboardViewport);
    window.addEventListener("resize", updateKeyboardViewport);
    window.addEventListener("orientationchange", updateKeyboardViewport);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.visualViewport?.removeEventListener("resize", updateKeyboardViewport);
      window.visualViewport?.removeEventListener("scroll", updateKeyboardViewport);
      window.removeEventListener("resize", updateKeyboardViewport);
      window.removeEventListener("orientationchange", updateKeyboardViewport);
    };
  }, []);

  return keyboardViewport;
};

const AskMobile = ({
  onQueryAction,
  initialTextValue,
  guestMode = false,
  guestHeader = null,
  onDraftChange,
  draftRef,
}) => {
  const {
    isChatMode,
    chatHistory,
    isAIResponding,
    isChatDone,
    chatErrorMessage,
    guestRequestUsed,
  } = useStore();
  const screenWidth = useScreenWidth();
  const isCompactMobile = screenWidth < 600;
  const {
    keyboardInset,
    viewportHeight,
    viewportOffsetTop,
  } = useKeyboardViewport();
  const chatScrollAreaRef = useRef(null);
  const [textValue, setTextValue] = useState(initialTextValue || "");
  const [isMobileChatInputFocused, setIsMobileChatInputFocused] = useState(false);
  const [isSearchHistoryCollapsed, setIsSearchHistoryCollapsed] = useState(true);
  const [mobileInputBlurSignal, setMobileInputBlurSignal] = useState(0);
  const chatInputRef = useRef(null);
  const [directInputRequested, setDirectInputRequested] = useState(false);

  useEffect(() => {
    setTextValue(initialTextValue || "");
  }, [initialTextValue]);

  useEffect(() => {
    setDirectInputRequested(false);
  }, [chatHistory, isChatMode]);

  useEffect(() => {
    if (!isChatMode) {
      setIsMobileChatInputFocused(false);
      setIsSearchHistoryCollapsed(true);
    }
  }, [isChatMode]);

  useEffect(() => {
    if (isChatDone && !isMobileChatInputFocused) {
      setIsSearchHistoryCollapsed(true);
    }
  }, [isChatDone, isMobileChatInputFocused]);

  useEffect(() => {
    if (!isChatMode) return undefined;
    return lockDocumentScroll();
  }, [isChatMode]);

  useEffect(() => {
    if (!isChatMode || !chatScrollAreaRef.current) return undefined;

    return scheduleChatScrollToBottom({
      getScrollArea: () => chatScrollAreaRef.current,
      requestFrame: window.requestAnimationFrame.bind(window),
      cancelFrame: window.cancelAnimationFrame.bind(window),
    });
  }, [
    chatErrorMessage,
    chatHistory,
    isAIResponding,
    isChatDone,
    isChatMode,
    isMobileChatInputFocused,
    isSearchHistoryCollapsed,
    keyboardInset,
    viewportHeight,
  ]);

  useEffect(() => {
    if (!isChatMode) return undefined;

    return observeChatResize({
      scrollArea: chatScrollAreaRef.current,
      ResizeObserverClass: window.ResizeObserver,
    });
  }, [isChatMode]);

  const handleMobileInputFocusChange = (focused) => {
    setIsMobileChatInputFocused(focused);
    if (focused) {
      setIsSearchHistoryCollapsed(true);
    }
  };

  const handleKeywordClick = (exampleText) => {
    setTextValue(exampleText);
  };

  const handleRequestDirectInput = () => {
    chatInputRef.current?.focus({ preventScroll: true });
    setDirectInputRequested(true);
  };

  const handleMobileSearchHistoryToggle = () => {
    if (isSearchHistoryCollapsed) {
      setIsSearchHistoryCollapsed(false);
      setIsMobileChatInputFocused(false);
      setMobileInputBlurSignal(prev => prev + 1);
      return;
    }

    setIsSearchHistoryCollapsed(true);
    setIsMobileChatInputFocused(false);
    setMobileInputBlurSignal(prev => prev + 1);
  };

  if (isChatMode) {
    return (
      <ChatModeWrapper
        $viewportHeight={viewportHeight}
        $viewportOffsetTop={viewportOffsetTop}
        data-testid="mobile-chat-root"
      >
        {guestMode ? guestHeader : <BannerMobile isChatHeader />}
        <ChatScrollArea
          ref={chatScrollAreaRef}
          $guestMode={guestMode}
          $isInputFocused={isMobileChatInputFocused}
          data-testid="chat-scroll-area"
        >
          <ChatScrollInner data-testid="chat-scroll-inner">
            <ChatContainer guestMode={guestMode} onRequestDirectInput={handleRequestDirectInput} />
          </ChatScrollInner>
        </ChatScrollArea>
        <ChatSearchHistoryDock
          $keyboardInset={keyboardInset}
          $isInputFocused={isMobileChatInputFocused}
        >
          <ChatSearchHistory
            isMobileMode
            isCollapsible={isCompactMobile}
            isCollapsed={isCompactMobile && isSearchHistoryCollapsed}
            onToggleCollapse={handleMobileSearchHistoryToggle}
          />
        </ChatSearchHistoryDock>
        <ChatBottomBar
          $keyboardInset={keyboardInset}
          $isInputFocused={isMobileChatInputFocused}
        >
          <AskForm
            onQueryAction={onQueryAction}
            initialTextValue={textValue}
            onMobileInputFocusChange={handleMobileInputFocusChange}
            mobileInputBlurSignal={mobileInputBlurSignal}
            focusRef={chatInputRef}
            directInputRequested={directInputRequested}
            guestMode={guestMode}
            onDraftChange={onDraftChange}
            draftRef={draftRef}
          />
        </ChatBottomBar>
      </ChatModeWrapper>
    );
  }

  return (
    <Wrapper data-floating-intro={guestMode ? "true" : undefined}>
      <Info onKeywordClick={handleKeywordClick} />
      <HeightInterval
        height={screenWidth >= 600 ? 16 : guestMode ? 12 : 98}
      />
      <InitialComposer
        $keyboardInset={keyboardInset}
        $isInputFocused={isMobileChatInputFocused}
      >
        <AskForm
          onQueryAction={onQueryAction}
          initialTextValue={textValue}
          onMobileInputFocusChange={handleMobileInputFocusChange}
          guestMode={guestMode}
          onDraftChange={onDraftChange}
          draftRef={draftRef}
        />
        {guestMode && !guestRequestUsed && (
          <GuestPolicyNotice>
            로그인 없이 대화 1회 무료
          </GuestPolicyNotice>
        )}
        {!guestMode && <Notice>
          *기재한 내용은 AI 학습 데이터로 수집 및 활용되지 않습니다.
        </Notice>}
      </InitialComposer>
    </Wrapper>
  );
};

const ChatModeWrapper = styled.div`
  position: fixed;
  top: ${({ $viewportOffsetTop }) => `${$viewportOffsetTop}px`};
  right: 0;
  left: 0;
  z-index: 10;
  display: flex;
  flex-direction: column;
  height: ${({ $viewportHeight }) => ($viewportHeight ? `${$viewportHeight}px` : '100vh')};
  height: ${({ $viewportHeight }) => ($viewportHeight ? `${$viewportHeight}px` : '100dvh')};
  max-height: ${({ $viewportHeight }) => ($viewportHeight ? `${$viewportHeight}px` : '100dvh')};
  width: 100%;
  overflow: hidden;
  min-height: 0;
  background: #fff;
  transition: height 0.18s ease, max-height 0.18s ease;
`;

const ChatScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 8px 0 ${({ $isInputFocused }) => ($isInputFocused ? '8px' : '0')};
  overscroll-behavior-y: contain;
  -webkit-overflow-scrolling: touch;

  @media (max-width: 599px) {
    padding-top: ${({ $guestMode }) => ($guestMode ? 'calc(54px + env(safe-area-inset-top, 0px))' : '8px')};
  }

  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.08);
    border-radius: 2px;
  }
`;

const ChatScrollInner = styled.div`
  min-height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
`;

const ChatSearchHistoryDock = styled.div`
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  width: 100%;
  overflow-x: hidden;
  padding: 8px 0 20px;
  background: #fff;
`;

const ChatBottomBar = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  overflow-x: hidden;
  padding: 12px 0 ${({ $isInputFocused }) => ($isInputFocused ? '16px' : '8px')};
  background: #fff;
  border-top: 0.3px solid rgba(0, 0, 0, 0.08);
  transition: padding-bottom 0.18s ease;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: auto;
  height: 100%;

  @media (max-width: 1140px) {
    height: auto;
  }
`;

const InitialComposer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  scroll-margin-bottom: ${({ $keyboardInset }) => `${$keyboardInset + 24}px`};
`;

const Notice = styled.div`
  color: rgba(0, 0, 0, 0.3);
  font-family: Pretendard;
  font-size: ${({ $compact }) => ($compact ? "11px" : "12px")};
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  margin-top: ${({ $compact }) => ($compact ? "4px" : "10px")};
  text-align: center;
`;

const GuestPolicyNotice = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  margin-top: 7px;
  padding: 0 24px;
  color: rgba(0, 0, 0, 0.58);
  font-family: Pretendard;
  font-size: 11px;
  font-weight: 600;
  line-height: 16px;
  letter-spacing: -0.22px;
  text-align: center;

  span {
    color: rgba(0, 0, 0, 0.42);
    font-weight: 400;
  }
`;

export default AskMobile;
